/**
 * Horse Assignment Modal Component
 * Allows managers to assign horses to players for events
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Alert,
  Chip,
  Box,
  LinearProgress,
  IconButton,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Horse, HorseAssignment } from "./types";
import HorseIcon from "./HorseIcon";

interface HorseAssignmentWithColor extends HorseAssignment {
  horse_color?: string;
}

interface HorseAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  devPort: string;
  clubId: number;
  eventType: "games" | "lessons" | "tournaments";
  eventId: string | number;
  playerId: string | number;
  playerName: string;
  maxChukkers?: number; // For games/tournaments, max chukkers player needs
  onAssignmentChange?: () => void; // Legacy: called on close if changes made
  onAssignmentsChange?: (assignments: HorseAssignmentWithColor[]) => void; // New: pass updated assignments
  managerUserId?: string | null; // Manager's user ID to include their horses
}

interface HorseWithAssignment extends Horse {
  assignment?: HorseAssignment;
  currentlyAssignedChukkers: number;
}

function HorseAssignmentModal({
  open,
  onClose,
  devPort,
  clubId,
  eventType,
  eventId,
  playerId,
  playerName,
  maxChukkers = 4,
  onAssignmentChange,
  onAssignmentsChange,
  managerUserId,
}: HorseAssignmentModalProps) {
  const [allHorses, setAllHorses] = useState<Horse[]>([]);
  const [allAssignmentsForEvent, setAllAssignmentsForEvent] = useState<HorseAssignment[]>([]);
  const [playerAssignments, setPlayerAssignments] = useState<HorseAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState<string>("");
  const [chukkersToAssign, setChukkersToAssign] = useState(1);
  const [error, setError] = useState("");
  const [clubManagerId, setClubManagerId] = useState<string | null>(managerUserId || null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const tempIdCounter = useRef(0);

  const isLesson = eventType === "lessons";

  // Fetch club manager ID if not provided
  useEffect(() => {
    if (!managerUserId && clubId) {
      fetch(`${devPort}get_club_manager/${clubId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data && data[0] && data[0][0]) {
            setClubManagerId(String(data[0][0]));
          }
        })
        .catch(() => {});
    } else if (managerUserId) {
      setClubManagerId(managerUserId);
    }
  }, [devPort, clubId, managerUserId]);

  // Load all available horses and current assignments
  const loadData = useCallback((opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    if (!silent) setError("");

    // Build list of fetch promises - horses are now at user level
    const fetchPromises: Promise<any>[] = [
      // Get player's own horses (user-level, reusable across clubs)
      fetch(`${devPort}horses/user/available/${playerId}`, {
        credentials: "include",
      }).then((r) => r.json()),
      // Get all assignments for this event (to calculate horse usage)
      fetch(`${devPort}horses/assignments/${clubId}/${eventType}/${eventId}`).then((r) => r.json()),
      // Get this player's assignments
      fetch(`${devPort}horses/player-assignments/${clubId}/${eventType}/${eventId}/${playerId}`).then((r) => r.json()),
    ];

    // Fetch club/manager horses (now stored at manager's user level)
    if (clubManagerId && String(clubManagerId) !== String(playerId)) {
      fetchPromises.push(
        fetch(`${devPort}horses/user/available/${clubManagerId}`, {
          credentials: "include",
        }).then((r) => r.json())
      );
    }

    Promise.all(fetchPromises)
      .then(([playerHorses, eventAssignments, playerAssigns, managerHorses]) => {
        // Combine manager's horses (club horses) and player's own horses (avoid duplicates)
        const allHorsesArray: Horse[] = [];
        const existingIds = new Set<string>();
        
        // Add manager/club horses first
        const managerHorsesArray = Array.isArray(managerHorses) ? managerHorses : [];
        for (const h of managerHorsesArray) {
          if (!existingIds.has(h.id)) {
            allHorsesArray.push(h);
            existingIds.add(h.id);
          }
        }
        
        // Add player's own horses
        const playerHorsesArray = Array.isArray(playerHorses) ? playerHorses : [];
        for (const h of playerHorsesArray) {
          if (!existingIds.has(h.id)) {
            allHorsesArray.push(h);
            existingIds.add(h.id);
          }
        }
        
        setAllHorses(allHorsesArray);
        setAllAssignmentsForEvent(Array.isArray(eventAssignments) ? eventAssignments : []);
        setPlayerAssignments(Array.isArray(playerAssigns) ? playerAssigns : []);
        if (!silent) setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load horse data:", err);
        if (!silent) setError("Failed to load horse data");
        if (!silent) setLoading(false);
      });
  }, [devPort, clubId, eventType, eventId, playerId, clubManagerId]);

  useEffect(() => {
    // Only load data when modal is open AND we have the manager ID (or it's been determined we don't need it)
    if (open && (clubManagerId || managerUserId)) {
      loadData();
      setSelectedHorse("");
      setChukkersToAssign(1);
      setHasLocalChanges(false);
      setSubmitting(false);
    }
  }, [open, loadData, clubManagerId, managerUserId]);

  // Calculate horse usage for this event (excluding this player's usage for that horse)
  const getHorseUsageForEventExcludingPlayer = (horseId: string): number => {
    return allAssignmentsForEvent
      .filter((a) => a.horse_id === horseId && String(a.player_id) !== String(playerId))
      .reduce((sum, a) => sum + a.chukkers_assigned, 0);
  };

  // Calculate total horse usage for this event (including all players)
  const getHorseUsageForEvent = (horseId: string): number => {
    return allAssignmentsForEvent
      .filter((a) => a.horse_id === horseId)
      .reduce((sum, a) => sum + a.chukkers_assigned, 0);
  };

  // Calculate remaining capacity for a horse (how many more chukkers can be assigned)
  const getRemainingCapacity = (horse: Horse): number => {
    const totalUsage = getHorseUsageForEvent(horse.id);
    const capacity = horse.game_chukkers || 2;
    return Math.max(0, capacity - totalUsage);
  };

  // Get horses that are assigned to this player
  const assignedHorses: HorseWithAssignment[] = playerAssignments.map((assignment) => {
    const horse = allHorses.find((h) => h.id === assignment.horse_id);
    return {
      ...(horse || { 
        id: assignment.horse_id, 
        name: assignment.horse_name, 
        color: "Bay", 
        game_chukkers: 2, 
        sex: "gelding" as const, 
        owner_id: null, 
        owner_type: "club" as const, 
        status: "available" as const, 
        notes: "", 
        created_at: "", 
        updated_at: "" 
      }),
      assignment,
      currentlyAssignedChukkers: assignment.chukkers_assigned,
    };
  });

  // Check if a horse is assigned to another player (for lessons only)
  const isHorseAssignedToOtherPlayer = (horseId: string): boolean => {
    if (!isLesson) return false; // Only apply to lessons
    return allAssignmentsForEvent.some(
      (a) => a.horse_id === horseId && String(a.player_id) !== String(playerId)
    );
  };

  // Get all horses (including those assigned to other players for lessons) for display
  // This allows us to show them greyed out in the dropdown
  const allHorsesForDisplay = allHorses.filter((horse) => {
    const alreadyAssigned = playerAssignments.some((a) => a.horse_id === horse.id);
    const isManagersHorse = horse.owner_id && String(horse.owner_id) === String(clubManagerId);
    const isOwnedByTargetPlayer = horse.owner_id && String(horse.owner_id) === String(playerId);
    const canAssign = isManagersHorse || isOwnedByTargetPlayer || !horse.owner_id;
    return !alreadyAssigned && canAssign;
  });

  // Total chukkers currently assigned to this player
  const totalAssignedChukkers = playerAssignments.reduce(
    (sum, a) => sum + a.chukkers_assigned,
    0
  );

  // Remaining chukkers the player still needs
  const remainingChukkersNeeded = Math.max(0, maxChukkers - totalAssignedChukkers);

  // Handle updating an existing assignment
  const handleUpdateAssignment = (assignmentId: string, horseId: string, newChukkers: number) => {
    if (newChukkers < 1) {
      // If setting to 0, remove the assignment
      handleRemoveAssignment(assignmentId);
      return;
    }

    // Use update endpoint instead of remove/create to avoid duplicates
    setSubmitting(true);
    fetch(devPort + "horses/update-assignment", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        assignment_id: assignmentId,
        chukkers_assigned: newChukkers,
        chukker_numbers: [],
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        setSubmitting(false);
        if (data.error) {
          setError(data.error);
        } else {
          setHasLocalChanges(true);
          // Keep modal stable: reconcile in background without showing a full reload spinner.
          loadData({ silent: true });
        }
      })
      .catch((err) => {
        setSubmitting(false);
        setError(err.message || "Failed to update assignment");
      });
  };

  // Handle adding a new horse assignment
  const handleAssign = () => {
    if (!selectedHorse) {
      setError("Please select a horse");
      return;
    }

    const body = {
      club_id: clubId,
      event_type: eventType,
      event_id: eventId,
      horse_id: selectedHorse,
      player_id: playerId,
      chukkers_assigned: isLesson ? 1 : chukkersToAssign,
      chukker_numbers: [],
    };

    setSubmitting(true);
    fetch(devPort + "horses/assign", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((data) => {
        setSubmitting(false);
        if (data.error) {
          setError(data.error);
        } else {
          // Optimistic UI: add a temporary assignment locally so the dialog doesn't "flash"
          const horse = allHorses.find((h) => h.id === selectedHorse);
          const tempId = `tmp-${Date.now()}-${tempIdCounter.current++}`;
          const chukkers = isLesson ? 1 : chukkersToAssign;
          const optimistic: HorseAssignment = {
            id: tempId,
            horse_id: selectedHorse,
            horse_name: horse?.name || "",
            player_id: playerId,
            chukkers_assigned: chukkers,
            chukker_numbers: [],
            assigned_at: new Date().toISOString(),
            // API may also provide horse_color; it's optional on the type.
            ...(horse?.color ? { horse_color: horse.color } : {}),
          } as HorseAssignment;

          setPlayerAssignments((prev) => [...prev, optimistic]);
          setAllAssignmentsForEvent((prev) => [...prev, optimistic]);
          setHasLocalChanges(true);

          setSelectedHorse("");
          setChukkersToAssign(1);
          // Reconcile with server in background (no spinner / no parent-triggered remount)
          loadData({ silent: true });
        }
      })
      .catch(() => {
        setSubmitting(false);
        setError("Failed to assign horse");
      });
  };

  const handleRemoveAssignment = (assignmentId: string) => {
    // Optimistic UI: remove locally first to avoid flicker
    setPlayerAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
    setAllAssignmentsForEvent((prev) => prev.filter((a) => a.id !== assignmentId));
    setHasLocalChanges(true);
    setSubmitting(true);
    fetch(devPort + "horses/remove-assignment", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        club_id: clubId,
        event_type: eventType,
        event_id: eventId,
        assignment_id: assignmentId,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        setSubmitting(false);
        if (data.error) {
          setError(data.error);
        } else {
          // Reconcile with server in background without spinner
          loadData({ silent: true });
        }
      })
      .catch(() => {
        setSubmitting(false);
        setError("Failed to remove assignment");
        // If remove failed, force a background refresh to restore correct state
        loadData({ silent: true });
      });
  };

  // Calculate max for the new assignment slider
  const getMaxForNewAssignment = (): number => {
    if (!selectedHorse) return 1;
    const horse = allHorses.find((h) => h.id === selectedHorse);
    if (!horse) return 1;
    const horseRemaining = getRemainingCapacity(horse);
    const maxValue = Math.min(remainingChukkersNeeded, horseRemaining);
    console.log(`getMaxForNewAssignment: horse=${horse.name}, horseCapacity=${horse.game_chukkers}, horseRemaining=${horseRemaining}, remainingChukkersNeeded=${remainingChukkersNeeded}, maxValue=${maxValue}`);
    return Math.max(1, maxValue);
  };

  // Calculate max for editing an existing assignment
  const getMaxForExistingAssignment = (horse: HorseWithAssignment): number => {
    // Current chukkers + remaining capacity + remaining player needs
    const currentAssigned = horse.assignment?.chukkers_assigned || 0;
    // Usage by OTHER players (not this player for this horse)
    const otherUsage = getHorseUsageForEventExcludingPlayer(horse.id);
    const horseCapacity = (horse.game_chukkers || 2) - otherUsage;
    const playerRemaining = remainingChukkersNeeded + currentAssigned;
    const maxValue = Math.min(playerRemaining, horseCapacity);
    console.log(`getMaxForExistingAssignment: horse=${horse.name}, horseCapacity=${horse.game_chukkers}, otherUsage=${otherUsage}, currentAssigned=${currentAssigned}, playerRemaining=${playerRemaining}, maxValue=${maxValue}`);
    return Math.max(1, maxValue);
  };

  const handleClose = () => {
    // Important: don't trigger parent refreshes while the modal is open.
    // Many parents re-fetch the entire signup table, which can remount this modal (looks like a "close/reopen").
    if (hasLocalChanges) {
      // New callback: pass the updated assignments array so parent can update local state without re-fetching
      if (onAssignmentsChange) {
        // Format assignments with horse_color for display
        const assignmentsWithColor: HorseAssignmentWithColor[] = playerAssignments.map((a) => {
          const horse = allHorses.find((h) => h.id === a.horse_id);
          return {
            ...a,
            horse_color: a.horse_color || horse?.color || "Bay",
          };
        });
        onAssignmentsChange(assignmentsWithColor);
      }
      // Legacy callback for backward compatibility
      if (onAssignmentChange) {
        onAssignmentChange();
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>Assign Horse to {playerName}</span>
          {!isLesson && (
            <Chip
              label={`${totalAssignedChukkers}/${maxChukkers} chukkers assigned`}
              color={totalAssignedChukkers >= maxChukkers ? "success" : "warning"}
              size="small"
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LinearProgress />
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
            {submitting && <LinearProgress />}

            {/* Current Assignments - Editable */}
            {assignedHorses.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Assigned Horses
                </Typography>
                <Stack spacing={2}>
                  {assignedHorses.map((horse) => {
                    const maxSlider = getMaxForExistingAssignment(horse);
                    return (
                      <Card key={horse.id} variant="outlined">
                        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <HorseIcon color={horse.color} size={36} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2">{horse.name}</Typography>
                              {!isLesson && horse.assignment && (
                                <Stack spacing={0.5} sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Chukkers: {horse.assignment.chukkers_assigned} (max: {maxSlider}, horse limit: {horse.game_chukkers})
                                  </Typography>
                                  <Slider
                                    value={horse.assignment.chukkers_assigned}
                                    onChange={(_, value) => {
                                      // Update local state for immediate visual feedback
                                      if (horse.assignment) {
                                        // Update the assignment in local state
                                        setPlayerAssignments(prev => 
                                          prev.map(a => 
                                            a.id === horse.assignment!.id 
                                              ? { ...a, chukkers_assigned: value as number }
                                              : a
                                          )
                                        );
                                      }
                                    }}
                                    onChangeCommitted={(_, value) => {
                                      // Only update API when user releases the slider
                                      if (horse.assignment) {
                                        handleUpdateAssignment(
                                          horse.assignment.id,
                                          horse.id,
                                          value as number
                                        );
                                      }
                                    }}
                                    min={0}
                                    max={maxSlider}
                                    step={1}
                                    marks
                                    size="small"
                                    valueLabelDisplay="auto"
                                  />
                                </Stack>
                              )}
                              {isLesson && (
                                <Typography variant="caption" color="success.main">
                                  Assigned
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => horse.assignment && handleRemoveAssignment(horse.assignment.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {/* Add New Assignment */}
            {((remainingChukkersNeeded > 0 || isLesson) && assignedHorses.length === 0) || (remainingChukkersNeeded > 0 && !isLesson) ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {assignedHorses.length > 0 ? "Add Another Horse" : "Assign a Horse"}
                </Typography>

                {allHorsesForDisplay.length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    No additional horses available. All horses are either already assigned to this player or at capacity for this event.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Horse</InputLabel>
                      <Select
                        value={selectedHorse}
                        label="Select Horse"
                        onChange={(e) => {
                          const horseId = e.target.value;
                          // Only allow selection of available horses
                          const selectedHorseObj = allHorses.find((h) => h.id === horseId);
                          if (selectedHorseObj) {
                            const isDisabled = isHorseAssignedToOtherPlayer(horseId) || 
                                             getRemainingCapacity(selectedHorseObj) === 0;
                            if (!isDisabled) {
                              setSelectedHorse(horseId);
                              setChukkersToAssign(1);
                            }
                          }
                        }}
                      >
                        {allHorsesForDisplay.map((horse) => {
                          const remaining = getRemainingCapacity(horse);
                          const assignedToOtherPlayer = isHorseAssignedToOtherPlayer(horse.id);
                          const isDisabled = assignedToOtherPlayer || remaining === 0;
                          
                          return (
                            <MenuItem 
                              key={horse.id} 
                              value={horse.id}
                              disabled={isDisabled}
                              sx={{
                                opacity: isDisabled ? 0.5 : 1,
                                '&.Mui-disabled': {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                sx={{ width: "100%" }}
                              >
                                <HorseIcon color={horse.color} size={24} />
                                <span>{horse.name}</span>
                                <Chip
                                  size="small"
                                  label={horse.sex === "mare" ? "♀" : "♂"}
                                  variant="outlined"
                                  sx={{ minWidth: 30 }}
                                />
                                <Box sx={{ flex: 1 }} />
                                {assignedToOtherPlayer && isLesson ? (
                                  <Chip
                                    size="small"
                                    label="Assigned to another player"
                                    color="error"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label={`${remaining}/${horse.game_chukkers} available`}
                                    color={remaining > 0 ? "primary" : "default"}
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    {!isLesson && selectedHorse && (
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          Chukkers to assign: <strong>{chukkersToAssign}</strong>
                          {" "}(max: {getMaxForNewAssignment()}, player needs: {remainingChukkersNeeded})
                        </Typography>
                        <Slider
                          value={chukkersToAssign}
                          onChange={(_, value) => setChukkersToAssign(value as number)}
                          min={1}
                          max={getMaxForNewAssignment()}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                        />
                      </Stack>
                    )}

                    {selectedHorse && (() => {
                      const selectedHorseObj = allHorses.find((h) => h.id === selectedHorse);
                      const isSelectedHorseDisabled = selectedHorseObj && (
                        isHorseAssignedToOtherPlayer(selectedHorse) || 
                        getRemainingCapacity(selectedHorseObj) === 0
                      );
                      return (
                        <Button
                          onClick={handleAssign}
                          variant="contained"
                          color="success"
                          startIcon={<PlayCircleOutlineIcon />}
                          disabled={isSelectedHorseDisabled || submitting}
                        >
                          Assign Horse
                        </Button>
                      );
                    })()}
                  </Stack>
                )}
              </Box>
            ) : totalAssignedChukkers >= maxChukkers ? (
              <Alert severity="success" variant="outlined">
                All {maxChukkers} chukkers have been assigned horses!
              </Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default HorseAssignmentModal;
